import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { usePowerPointTemplate, SlideContent } from 'common/hooks/usePowerPointExport';
import { widgetPptType } from '../widget-utils/ppt-export/pptWidgetConfig.ts';
import {
  PPTgenerateWidgetSlide,
  PPTgenerateSplitSlide,
} from '../widget-utils/ppt-export/pptUtils.ts';
import {
  PPTExportChartOptions,
  PPTWidgetData,
  SingleTileExportPPT,
  PPTMultiQuestionExportData,
  PageExportPPT,
} from '../../../models/pptx.model.ts';
import { useSelector } from 'react-redux';
import { selectAppliedFilters } from '../../../store/slices/common-filters.slice.ts';
import { DATASET_INITIAL_ID } from '../../../constants.ts';
import { selectEndDate, selectStartDate } from '../../../store/slices/date-filter.slice.ts';
import { RootState } from '../../../store/index.ts';

import { QuestionExportPPT } from '../../../models/pptx.model.ts';
import { useDispatch } from 'react-redux';
import {
  setExportTotal,
  setShowExportProgressBar,
} from '../../../store/slices/export-ppt.slice.ts';
import { useCallback, useMemo } from 'react';
import { WidgetModel } from '../../../models/widget.model.ts';
import { castToType, isReportViewMode } from '../../../hooks/utils.ts';
import { WidgetExportModel } from '../../../models/export.model.ts';
import useUniversalFilters from '../../../../modules/insights/hooks/useUniversalFilters.ts';
import { ServiceWorkerAction } from '../../../sw/constants.ts';
import { triggerWorkerAction } from '../../../sw/utils.ts';
import { useCompareUrlWithProjectId } from 'common/hooks/route.ts';
import { ALIGNMENT_ROUTES } from 'routes/routes.const.ts';
import { NotificationType, ToastState } from 'common/models/notification.model.ts';
import { showToast } from 'common/store/slices/notification.slice.ts';
import useFilterLabels from 'common/hooks/useFilterLabels.ts';
import { widgetHasData } from 'common/utils/widgetHasData.ts';

const PPT_EXPORT_SKIPPED_WIDGETS_TOAST: ToastState = {
  id: 'ppt-export-filtered-no-data-toast',
  title: 'Please Note: ',
  text: 'Questions without data will not be included in the export.',
  type: NotificationType.WARNING,
};

export const useSingleTileExportPPT = ({ widget, summaryBar, question }: SingleTileExportPPT) => {
  const { compareUrl } = useCompareUrlWithProjectId();
  const isAlignment = compareUrl(ALIGNMENT_ROUTES.root, { includes: true });
  const { activeModule, activeProject } = useModuleProjectContext();
  const { generatePowerPoint } = usePowerPointTemplate(); // Optional integration
  const reduxFilter = useSelector((state: RootState) =>
    selectAppliedFilters(state, DATASET_INITIAL_ID)
  );
  const reduxStartDate = useSelector((state: RootState) =>
    selectStartDate(state, DATASET_INITIAL_ID)
  );
  const reduxEndDate = useSelector((state: RootState) => selectEndDate(state, DATASET_INITIAL_ID));
  const filter = widget.defaultFilters?.filters ?? reduxFilter;
  const startDate = widget.defaultFilters?.startDate ?? reduxStartDate;
  const endDate = widget.defaultFilters?.endDate ?? reduxEndDate;
  const appliedFilter = {
    reduxFilter: filter,
    startDate: startDate,
    endDate: endDate,
  };
  const exportWidgetPPT = async (data: PPTWidgetData, chartOptions: PPTExportChartOptions) => {
    const widgetType = widgetPptType[widget.type];
    if (!widgetType) throw new Error(`Unsupported chart type: ${widget.type}`);

    const fileName = `${activeProject?.projectDisplayName}-${activeModule?.moduleName}-${widget.title}.pptx`;
    const slides = PPTgenerateWidgetSlide({
      widget,
      data,
      chartOptions,
      summaryBar,
      question,
      appliedFilter,
      isAlignment,
    });
    await generatePowerPoint({
      presentation: {
        fileName,
        title: activeModule?.moduleName ?? '',
        subtitle: activeProject?.projectDisplayName ?? '',
      },
      slides,
      appliedFilter: appliedFilter,
    });
  };

  return { exportWidgetPPT };
};

export const useQuestionExportPPT = () => {
  const dispatch = useDispatch();

  const exportMultiQuestionPPT = ({
    questions,
    leftChartJson,
    rightChartJson,
    summaryBar,
    oppositeLeftChart,
    oppositeRightChart,
    oppositeSummaryBar,
    apiUrl,
  }: QuestionExportPPT) => {
    dispatch(setExportTotal(questions.length));
    dispatch(setShowExportProgressBar(true));
    triggerWorkerAction<QuestionExportPPT>(ServiceWorkerAction.ExportPptAction, {
      apiUrl,
      questions,
      leftChartJson: {
        widgetRequestWithFilters: leftChartJson.widgetRequestWithFilters,
        widget: leftChartJson.widget,
      },
      rightChartJson: {
        widgetRequestWithFilters: rightChartJson.widgetRequestWithFilters,
        widget: rightChartJson.widget,
      },
      summaryBar: {
        widgetRequestWithFilters: summaryBar.widgetRequestWithFilters,
        widget: summaryBar.widget,
      },
      oppositeLeftChart: {
        widgetRequestWithFilters: oppositeLeftChart.widgetRequestWithFilters,
        widget: oppositeLeftChart.widget,
      },
      oppositeRightChart: {
        widgetRequestWithFilters: oppositeRightChart.widgetRequestWithFilters,
        widget: oppositeRightChart.widget,
      },
      oppositeSummaryBar: {
        widgetRequestWithFilters: oppositeSummaryBar.widgetRequestWithFilters,
        widget: oppositeSummaryBar.widget,
      },
    });
  };

  return { exportMultiQuestionPPT };
};

export const useMultiTileSplitExportPPT = () => {
  const { compareUrl } = useCompareUrlWithProjectId();
  const isAlignment = compareUrl(ALIGNMENT_ROUTES.root, { includes: true });
  const { activeModule, activeProject } = useModuleProjectContext();
  const { generatePowerPoint } = usePowerPointTemplate();
  const dispatch = useDispatch();
  const filterLabels = useFilterLabels();

  const reduxFilter = useSelector((state: RootState) =>
    selectAppliedFilters(state, DATASET_INITIAL_ID)
  );
  const reduxStartDate = useSelector((state: RootState) =>
    selectStartDate(state, DATASET_INITIAL_ID)
  );
  const reduxEndDate = useSelector((state: RootState) => selectEndDate(state, DATASET_INITIAL_ID));

  const appliedFilter = useMemo(
    () => ({
      reduxFilter: reduxFilter,
      startDate: reduxStartDate,
      endDate: reduxEndDate,
    }),
    [reduxFilter, reduxStartDate, reduxEndDate]
  );
  const exportMultiTilePPT = useCallback(
    async (results: PPTMultiQuestionExportData[]): Promise<boolean> => {
      try {
        const fileName = `${activeProject?.projectDisplayName}-${activeModule?.moduleName}-summary.pptx`;
        const slides: SlideContent[] = [];

        const normalizedResults: PPTMultiQuestionExportData[] = Array.isArray(results)
          ? results
          : Object.values(results);

        const selectedCategory =
          filterLabels?.find((f) => f.label === 'Category')?.value?.[0] ?? null;
        const selectedQuestion =
          filterLabels?.find((f) => f.label === 'Question')?.value?.[0] ?? null;

        let filteredCategoryHasNoData = false;
        let hasMissingSlides = false;
        let missingDataToastShown = false;
        for (let i = 0; i < normalizedResults.length; i++) {
          const current = normalizedResults[i];
          if (!current) continue;

          const { question, leftChart, rightChart, summaryBar } = current;

          const leftHasData = widgetHasData(leftChart.widget, leftChart.data);
          const rightHasData = widgetHasData(rightChart.widget, rightChart.data);
          // checking and skiping slides  that should be empty
          const hasAnyData = leftHasData || rightHasData;
          if (!hasAnyData) {
            filteredCategoryHasNoData = true;
            hasMissingSlides = true;
            if (!missingDataToastShown) {
              dispatch(showToast(PPT_EXPORT_SKIPPED_WIDGETS_TOAST));
              missingDataToastShown = true;
            }
            continue;
          }

          slides.push(
            PPTgenerateSplitSlide({
              question,
              leftWidget: leftChart.widget,
              rightWidget: rightChart.widget,
              leftWidgetData: leftChart.data,
              rightWidgetData: rightChart.data,
              projectName: activeProject?.projectDisplayName,
              summaryBar: summaryBar
                ? [{ response: summaryBar.data, widget: summaryBar.widget }]
                : [],
              appliedFilter,
              isAlignment,
            })
          );
        }

        if (filteredCategoryHasNoData && slides.length === 0) {
          const label = selectedQuestion
            ? `question "${selectedQuestion}"`
            : selectedCategory
              ? `category "${selectedCategory}"`
              : 'filters';

          dispatch(
            showToast({
              id: 'ppt-export-filtered-toast',
              title: 'No Data Available',
              text: `No chart data is available for the selected ${label}. Please adjust your filters and try again.`,
              type: NotificationType.WARNING,
            })
          );
          return false;
        }

        if (slides.length === 0) {
          return false;
        }

        await generatePowerPoint({
          presentation: {
            fileName,
            title: activeModule?.moduleName ?? '',
            subtitle: activeProject?.projectDisplayName ?? '',
          },
          slides,
          appliedFilter,
        });
        return !hasMissingSlides;
      } catch (err: any) {
        console.error('exportMultiTilePPT failed:', err);
        throw err;
      }
    },
    [
      activeModule,
      activeProject,
      generatePowerPoint,
      appliedFilter,
      isAlignment,
      filterLabels,
      dispatch,
    ]
  );

  return { exportMultiTilePPT };
};

export const usePageExportPPT = () => {
  const { compareUrl } = useCompareUrlWithProjectId();
  const isAlignment = compareUrl(ALIGNMENT_ROUTES.root, { includes: true });
  const { activeModule, activeProject } = useModuleProjectContext();
  const { generatePowerPoint } = usePowerPointTemplate(); // Optional integration
  const reduxFilter = useSelector((state: RootState) =>
    selectAppliedFilters(state, DATASET_INITIAL_ID)
  );
  const reduxStartDate = useSelector((state: RootState) =>
    selectStartDate(state, DATASET_INITIAL_ID)
  );
  const reduxEndDate = useSelector((state: RootState) => selectEndDate(state, DATASET_INITIAL_ID));
  const universalFilters = useUniversalFilters();
  const isReport = isReportViewMode();
  const appliedFilter = {
    reduxFilter: reduxFilter,
    startDate: reduxStartDate,
    endDate: reduxEndDate,
  };
  const exportPagePPT = async ({
    widgets,
    question,
    summaryBar,
    pageTitle,
    pageName,
    widgetOptions,
  }: PageExportPPT) => {
    if (!widgets || widgets.length === 0) {
      throw new Error('No widgets provided for export');
    }
    const fileName = `${activeProject?.projectDisplayName}-${activeModule?.moduleName}-${pageTitle}.pptx`;
    const slides: SlideContent[] = [];
    for (let index = 0; index < widgets.length; index++) {
      const data = widgets[index];
      const widget = castToType<WidgetModel>(data.widget);
      const widgetType = widgetPptType[widget?.type];
      if (!widgetType) throw new Error(`Unsupported chart type: ${widget?.type}`);
      const chartOptionsWidget = widgetOptions.find((widgetOption) => {
        return widgetOption.value.id === `${pageName}-${widget.id}`;
      });
      const chartOptions = castToType<PPTExportChartOptions>({
        barChartOptions: chartOptionsWidget?.value.kwelloBarChartOptions,
        lineChartOptions: chartOptionsWidget?.value.kwelloLineChartOptions,
      });
      slides.push(
        ...PPTgenerateWidgetSlide({
          widget,
          data: castToType<PPTWidgetData>(data.response),
          chartOptions,
          summaryBar: castToType<WidgetExportModel[]>(summaryBar),
          question,
          appliedFilter,
          universalFilters: universalFilters?.universalFilters,
          isReport,
          isAlignment,
        })
      );
    }
    await generatePowerPoint({
      presentation: {
        fileName,
        title: activeModule?.moduleName ?? '',
        subtitle: activeProject?.projectDisplayName ?? '',
      },
      slides,
      appliedFilter: isReport ? undefined : appliedFilter,
    });
  };
  return { exportPagePPT };
};
